import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Database, FolderInput, Loader2, RotateCcw, ShieldCheck, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { backupsApi } from '@/lib/backups-api';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { serversApi } from '@/lib/servers-api';
import { dialogSurface } from '@/components/surface/tones';
import { IconTile } from '@/components/surface/ui';
import type { BackupRecord, Server } from '@/types/api';

/**
 * Confirms and performs a restore.
 *
 * ## Typing the source's name is not theatre
 *
 * This is the one operation on the platform that destroys the customer's current state, and it is
 * reached from a list where every row looks the same. A checkbox is clicked without reading;
 * typing the name forces the person to look at *which* row they are on. The server requires it
 * too — a client that skipped the check could not restore anyway.
 *
 * ## The safety net is stated up front, not discovered afterwards
 *
 * The current files are moved aside rather than deleted, and the archive is checksummed before
 * anything is touched. Somebody about to do this under pressure should know both before they
 * decide, not read about it in the log afterwards.
 *
 * ## Restoring somewhere else
 *
 * The disaster this whole module exists for is losing the machine, so the destination server is a
 * choice rather than an assumption. Two things shape how it is offered:
 *
 * - It appears **only for an offsite backup**. A copy stored on the source's own server is
 *   gone with that server, so offering to restore it elsewhere would be offering something that
 *   cannot work. Where the option is missing, the dialog says why instead of staying silent.
 * - Choosing another server **moves the source**, and that is stated before the button, not
 *   discovered in the log afterwards. It is the honest consequence: after this the data is on the
 *   new machine and nowhere else. What used that data on the old machine — a web server, a service,
 *   database accounts — does not come with it, and the dialog says that too.
 *
 * ## It cannot be dismissed while it runs
 *
 * Escape and the close button do nothing while a restore is in flight, and a click outside never
 * closes it: losing the typed confirmation to a stray click would be an annoyance, and losing sight
 * of a running restore would be worse.
 */
export function RestoreDialog({
  backup,
  isOffsite,
  currentServerId,
  onClose,
  onRestored,
}: {
  backup: BackupRecord | null;
  /** Whether this backup's destination survives the loss of the server it came from. */
  isOffsite: boolean;
  /** The server the source is on now, so it can be marked rather than offered as a move. */
  currentServerId: string | null;
  onClose: () => void;
  onRestored: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [targetServerId, setTargetServerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[] | null>(null);

  useEffect(() => {
    setConfirmation('');
    setTargetServerId('');
    setError(null);
    setLog(null);
  }, [backup?.id]);

  // Only fetched when it can be acted on. A local backup has nowhere else to go, and a dialog that
  // loaded a server list it was never going to show would be a request nobody asked for.
  const servers = useQuery({
    queryKey: ['servers', 'all'],
    queryFn: () => serversApi.all(),
    enabled: backup !== null && isOffsite,
  });

  const choices: Server[] = (servers.data ?? []).filter((s) => s.ready);
  const movingToAnotherServer = targetServerId !== '' && targetServerId !== currentServerId;

  const restore = useMutation({
    mutationFn: () =>
      backupsApi.restore(
        backup?.id ?? '',
        confirmation.trim(),
        movingToAnotherServer ? targetServerId : undefined,
      ),
    onSuccess: (result) => {
      setError(null);
      setLog(result.log);
      onRestored();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  if (backup === null) {
    return null;
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !restore.isPending) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={`max-w-lg ${dialogSurface}`}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (restore.isPending) event.preventDefault();
        }}
      >
        <DialogHeader className="flex-row items-start gap-3 space-y-0">
          <IconTile icon={log === null ? AlertTriangle : CheckCircle2} tone={log === null ? 'danger' : 'success'} />
          <div className="min-w-0 space-y-1">
            <DialogTitle>Restore {backup.source_name}?</DialogTitle>
            <DialogDescription>From the backup taken {new Date(backup.started_at).toLocaleString()}.</DialogDescription>
          </div>
        </DialogHeader>

        {log === null ? (
          <div className="space-y-5">
            <Alert variant="warning" className="border-0">
              {/*
                * A database-only copy restores a dump and nothing else -- the files are untouched.
                * Showing the full-restore warning over it would describe a destruction that is not
                * going to happen, and the cost of that is not pedantry: somebody who has learned
                * the warning is exaggerated stops reading it before the restore where it is true.
                */}
              {!backup.replaces_files ? (
                <>
                  This replaces the source&rsquo;s <strong>database</strong> with the copy in this
                  backup. Its files are not part of this backup and will not be touched.
                </>
              ) : movingToAnotherServer ? (
                <>
                  This puts the source&rsquo;s files
                  {backup.included_database && ' and database'} onto the server you chose, and moves
                  the source there. The original server is left untouched.
                </>
              ) : (
                <>
                  This replaces the source&rsquo;s files
                  {backup.included_database && ' and its database'} with the contents of this
                  backup. Anything changed since then is lost.
                </>
              )}
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="restore-server">Restore to</Label>
              {isOffsite ? (
                <>
                  <Select
                    id="restore-server"
                    value={targetServerId}
                    onChange={(event) => setTargetServerId(event.target.value)}
                    disabled={restore.isPending || servers.isLoading}
                  >
                    <option value="">The server this source is on</option>
                    {choices
                      .filter((server) => server.id !== currentServerId)
                      .map((server) => (
                        <option key={server.id} value={server.id}>
                          {server.name} ({server.endpoint})
                        </option>
                      ))}
                  </Select>
                  {movingToAnotherServer && (
                    <p className="text-xs text-muted-foreground">
                      Only the data moves. Whatever used it on the old server, such as a web server,
                      a service or database accounts, is not set up on the new one; install and
                      configure those there afterwards.
                    </p>
                  )}
                </>
              ) : (
                <p className="rounded-lg bg-foreground/[0.04] px-3 py-2 text-sm text-muted-foreground">
                  Stored on the source&rsquo;s own server, so it can only be restored there. Add
                  cloud storage for copies that can move.
                </p>
              )}
            </div>

            <ul className="space-y-2.5 rounded-lg bg-foreground/[0.035] p-3.5">
              <Safeguard icon={ShieldCheck} tone="success">
                The archive is checked against its recorded checksum first. If it does not match, nothing is touched.
              </Safeguard>
              {backup.replaces_files && (
                <Safeguard icon={FolderInput} tone="success">
                  The current files are moved aside on the server, not deleted. You can get them
                  back if this turns out to be the wrong backup.
                </Safeguard>
              )}
              {backup.included_database && (
                <Safeguard icon={Database} tone="danger">
                  The database is overwritten. That part cannot be undone.
                </Safeguard>
              )}
            </ul>

            {error && <Alert variant="danger" className="border-0">{error}</Alert>}

            <div className="space-y-2">
              <Label htmlFor="restore-confirm">
                Type <span className="font-mono font-semibold">{backup.source_name}</span> to
                confirm
              </Label>
              <Input
                id="restore-confirm"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
                className="font-mono"
              />
            </div>

            <DialogFooter className="mt-0 border-t border-foreground/[0.07] pt-5">
              <Button variant="outline" onClick={onClose} disabled={restore.isPending}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => restore.mutate()}
                disabled={restore.isPending || confirmation.trim() !== backup.source_name}
              >
                {restore.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                Restore
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert variant="success" className="border-0">The restore finished.</Alert>
            <ul className="max-h-64 space-y-1 overflow-y-auto rounded-lg bg-foreground/[0.04] p-3 font-mono text-xs text-muted-foreground">
              {log.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {backup.replaces_files && (
              <p className="text-xs text-muted-foreground">
                The previous files are kept on the server under a .before-restore- directory.
              </p>
            )}
            <DialogFooter className="mt-0">
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Safeguard({ icon: Icon, tone, children }: { icon: LucideIcon; tone: 'success' | 'danger'; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-muted-foreground">
      <Icon className={cn('mt-0.5 size-4 shrink-0', tone === 'success' ? 'text-success' : 'text-danger')} aria-hidden />
      <span>{children}</span>
    </li>
  );
}
